import { useState } from "react";

interface UseManualPaginatedResourceParams<TFilters extends Record<string, any>> {
  initialFilters?: TFilters;
  /** If provided, used as initial draft and applied state (e.g. restored from URL). Reset still uses initialFilters. */
  initialAppliedFilters?: TFilters;
  initialPageSize?: number;
  /** Initial page number (default 1). Used when restoring from URL. */
  initialPage?: number;
}

/**
 * useManualPaginatedResource
 * Generic, reusable hook for managing pagination and filters with manual search.
 * Unlike usePaginatedResource, filters are only applied when applyFilters() is called.
 *
 * Use this when you want users to explicitly click a "Search" button to apply filters.
 */
export const useManualPaginatedResource = <TFilters extends Record<string, any>>({
  initialFilters = {} as TFilters,
  initialAppliedFilters,
  initialPageSize = 50,
  initialPage = 1,
}: UseManualPaginatedResourceParams<TFilters>) => {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const initial = initialAppliedFilters ?? initialFilters;

  // Draft filters (user is editing, not sent to API)
  const [draftFilters, setDraftFilters] = useState<TFilters>(initial);

  // Applied filters (sent to API, only updated when applyFilters is called)
  const [appliedFilters, setAppliedFilters] = useState<TFilters>(initial);

  const handlePageChange = (newPage: number) => setPage(newPage);
  
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleFilterChange = <K extends keyof TFilters>(
    key: K,
    value: TFilters[K]
  ) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Apply the current draft filters to the API query.
   * This is typically called when the user clicks a "Search" button.
   */
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1); // Reset to first page when applying filters
  };

  /**
   * Reset both draft and applied filters to initial state.
   * This is typically called when the user clicks a "Clear" button.
   */
  const resetFilters = () => {
    setDraftFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  return {
    page,
    pageSize,
    draftFilters,
    appliedFilters,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    applyFilters,
    resetFilters,
  };
};
